import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Map, useMap } from '@vis.gl/react-google-maps'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { DeckProps } from '@deck.gl/core'
import { IconLayer } from '@deck.gl/layers'
import { RecentVehicleLocationData } from 'interfaces/ISummaryReport'
import { svgToDataURL, createClusterSvg, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from 'tools/mapHelper'
import Supercluster from 'supercluster'
import { getLocationIcon, getTooltipData } from './recentVehicleMapUtils'

interface IRecentVehicleLocationProps {
  data: RecentVehicleLocationData[]
}

interface ClusteredPoint {
  position: [number, number]
  id: string
  isCluster: boolean
  pointCount?: number
  clusterId?: number
  eldDate?: string
  rowId?: number
  assetEldId?: string
}

const DeckGLOverlay = (props: DeckProps) => {
  const map = useMap()
  const overlay = useMemo(
    () =>
      new GoogleMapsOverlay({
        ...props,
        getCursor: ({ isHovering, isDragging }) => {
          if (map) {
            if (isHovering) {
              map.setOptions({ draggableCursor: 'pointer' })
            } else {
              map.setOptions({ draggableCursor: '' })
            }
          }
          return isDragging ? 'grabbing' : 'grab'
        },
        getTooltip: ({ object, layer }) => {
          return getTooltipData(object, layer)
        },
      }),
    [map]
  )

  useEffect(() => {
    if (map) {
      overlay.setMap(map)
      return () => {
        overlay.setMap(null)
        map.setOptions({ draggableCursor: '' })
      }
    }
  }, [map, overlay])

  useEffect(() => {
    overlay.setProps(props)
  }, [overlay, props])

  return null
}

const RecentVehicleLocation: React.FC<IRecentVehicleLocationProps> = ({
  data,
}) => {
  const map = useMap()
  const [defaultZoom] = useState(DEFAULT_MAP_ZOOM)
  const [defaultCenter] = useState(DEFAULT_MAP_CENTER)
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_MAP_ZOOM)

  useEffect(() => {
    if (!map) return

    const handleZoomChanged = () => {
      const zoom = map.getZoom()
      if (zoom !== undefined) {
        setCurrentZoom(zoom)
      }
    }
    const listener = map.addListener('zoom_changed', handleZoomChanged)
    return () => listener.remove()
  }, [map])

  const vehicleLocationData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data
      .filter(
        (item) =>
          item.location && item.location.latitude && item.location.longitude
      )
      .map((item, index) => ({
        position: [item.location.longitude, item.location.latitude],
        id: item.rowId,
        eldDate: item.eldDateUtc,
        assetEldId: item.assetEldId,
      }))
  }, [data])

  const geoJsonPoints = useMemo(() => {
    if (!vehicleLocationData || vehicleLocationData.length === 0) return []

    return vehicleLocationData.map((item) => ({
      type: 'Feature' as const,
      properties: {
        id: item.id,
        eldDate: item.eldDate,
        assetEldId: item.assetEldId,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: item.position,
      },
    }))
  }, [vehicleLocationData])

  const clusteredData = useMemo(() => {
    if (!geoJsonPoints.length) return []

    const supercluster = new Supercluster({
      radius: 80,
      maxZoom: 12,
      minZoom: 0,
      minPoints: 2,
    })

    supercluster.load(geoJsonPoints)

    const bbox = [-180, -85, 180, 85] as [number, number, number, number]
    const zoom = Math.floor(currentZoom)

    const clusters = supercluster.getClusters(bbox, zoom)

    return clusters.map((cluster): ClusteredPoint => {
      const [longitude, latitude] = cluster.geometry.coordinates

      if (cluster.properties?.cluster) {
        return {
          position: [longitude, latitude],
          id: `cluster-${cluster.properties.cluster_id}`,
          isCluster: true,
          pointCount: cluster.properties.point_count,
          clusterId: cluster.properties.cluster_id,
        }
      } else {
        const props = cluster.properties
        return {
          position: [longitude, latitude],
          id: props.id,
          isCluster: false,
          eldDate: props.eldDate,
          assetEldId: props.assetEldId,
        }
      }
    })
  }, [geoJsonPoints, currentZoom])

  const handleClusterClick = useCallback(
    (info: any) => {
      const { object } = info

      if (object.isCluster && map) {
        const supercluster = new Supercluster({
          radius: 80,
          maxZoom: 12,
          minZoom: 0,
          minPoints: 2,
        })

        supercluster.load(geoJsonPoints)

        const expansionZoom = supercluster.getClusterExpansionZoom(
          object.clusterId
        )

        map.setCenter({
          lat: object.position[1],
          lng: object.position[0],
        })
        map.setZoom(Math.min(expansionZoom + 1, 20))
      }
    },
    [map, geoJsonPoints]
  )

  const layers = useMemo(() => {
    if (!clusteredData || clusteredData.length === 0) return []

    return [
      new IconLayer({
        id: 'vehicle-locations-clustered',
        data: clusteredData,
        getIcon: (d) => {
          if (d.isCluster) {
            const actualSize =
              d.pointCount! < 10 ? 50 : d.pointCount! < 100 ? 65 : 80
            return {
              url: svgToDataURL(createClusterSvg(d.pointCount || 0)),
              width: actualSize,
              height: actualSize,
            }
          } else {
            return {
              url: getLocationIcon(),
              width: 100,
              height: 100,
            }
          }
        },
        sizeScale: 4,
        getSize: (d) => (d.isCluster ? 12 : 10),
        getPosition: (d) => d.position,
        pickable: true,
        onClick: (info) => {
          if (info.object?.isCluster) {
            handleClusterClick(info)
          }
        },
      }),
    ]
  }, [clusteredData, handleClusterClick])

  return (
    <div style={{ height: '350px', width: '100%' }}>
      <Map
        defaultZoom={defaultZoom}
        defaultCenter={defaultCenter}
        gestureHandling={'greedy'}
        disableDefaultUI={true}
        clickableIcons={false}
        mapId={'RECENT_VEHICLE_LOCATION_MAP'}
      >
        <DeckGLOverlay layers={layers} />
      </Map>
    </div>
  )
}

export default RecentVehicleLocation
